/**
 * GDPRPrivacySettings.js — P3: Privacy & Data settings panel
 * Shows data categories, retention periods, record counts, and export/delete actions.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GDPRPrivacySettings() {
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [exporting, setExport]  = useState(false);

  useEffect(() => {
    axios.get(`${API}/gdpr/data-summary`, { withCredentials: true })
      .then(r => setSummary(r.data))
      .catch(e => logError(e, { context: 'GDPRPrivacySettings.load' }))
      .finally(() => setLoading(false));
  }, []);

  const downloadExport = async () => {
    setExport(true);
    try {
      const r = await axios.get(`${API}/gdpr/export/zip`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url  = URL.createObjectURL(r.data);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `solelytax_data_export_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed. Please try again.'); }
    finally { setExport(false); }
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-100 rounded-xl" />;

  return (
    <div className="space-y-6" data-testid="gdpr-privacy-settings">
      <div>
        <h3 className="font-bold text-gray-900 text-lg">Privacy & Data</h3>
        <p className="text-sm text-gray-500 mt-1">
          Your rights under GDPR · Contact: {summary?.contact}
        </p>
      </div>

      {/* ToS status */}
      {summary?.tos_accepted && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-800 font-semibold">✓ Terms of Service accepted</p>
          <p className="text-xs text-green-600 mt-0.5">
            Version {summary.tos_version} · {new Date(summary.tos_accepted_at).toLocaleDateString()}
          </p>
        </div>
      )}

      {/* Data categories table */}
      <div>
        <h4 className="text-sm font-bold text-gray-700 mb-3">Data we store</h4>
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Retention</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.data_categories || []).map((cat, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2.5 text-gray-900 font-medium">{cat.category}</td>
                  <td className="px-4 py-2.5 text-gray-600">{cat.retention}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs hidden md:table-cell">{cat.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Record counts */}
      {summary?.record_counts && (
        <div>
          <h4 className="text-sm font-bold text-gray-700 mb-2">Your data snapshot</h4>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(summary.record_counts).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-black text-gray-900">{v.toLocaleString()}</p>
                <p className="text-xs text-gray-500 capitalize mt-0.5">{k.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your rights */}
      <div>
        <h4 className="text-sm font-bold text-gray-700 mb-2">Your rights</h4>
        <ul className="space-y-1">
          {(summary?.your_rights || []).map((r, i) => (
            <li key={i} className="text-sm text-gray-600 flex gap-2"><span>•</span><span>{r}</span></li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
        <button
          onClick={downloadExport}
          disabled={exporting}
          data-testid="download-data-export-btn"
          className="flex-1 border border-gray-300 text-gray-700 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition"
        >
          {exporting ? 'Preparing export…' : '⬇ Download All My Data (ZIP)'}
        </button>
        <a
          href="/settings?tab=delete-account"
          data-testid="delete-account-link"
          className="flex-1 border border-red-200 text-red-600 font-semibold text-sm py-2.5 rounded-xl hover:bg-red-50 text-center transition"
        >
          Delete My Account
        </a>
      </div>
    </div>
  );
}
